<template>
  <main-layout>
    <div class="pa-3">
      <v-layout wrap align-center class="mb-3">
        <div class="title">{{ $t('asset_wise.executive_users') }}</div>
        <v-spacer />
        <div>
          <v-btn outline color="primary" @click="addUser()">
            <v-icon>add</v-icon>
            {{ $t('Add') }}
          </v-btn>
        </div>
      </v-layout>

      <v-layout class="mb-3">
        <v-flex xs12 sm6>
          <div class="form-control" :style="{ position: 'relative' }">
            <input
              v-model="filter.search"
              type="text"
              :placeholder="$t('Search')"
              class="ma-0"
              :style="{ paddingLeft: '40px' }"
              @input="debounceSearch()"
            />
            <div
              :style="{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                display: 'flex',
                placeItems: 'center',
                padding: '0 10px',
                pointerEvents: 'none',
                fontSize: '16px'
              }"
            >
              <i class="fa fa-search fa-fw"></i>
            </div>
          </div>
        </v-flex>
      </v-layout>

      <DataTable
        v-model="result.items"
        :pagination.sync="result.pagination"
        :loading="result.isLoading"
        @edit:password="changePassword($event)"
        @edit:isConnected="(id, value) => setConnection(id, value)"
        @edit="editUser($event)"
        @remove="removeUser($event)"
        @update:pagination="search($event.page)"
      />
    </div>

    <UserFormDialog
      v-model="userFormDialog.isOpen"
      :loading="userFormDialog.isLoading"
      :title="userFormDialog.title"
      @input="handleUserFormDialogInput"
      @submit="submitUserForm"
    >
      <UserForm
        ref="userForm"
        :key="userFormDialog.key"
        v-model="userFormDialog.data"
        :mode="userFormDialog.mode"
      />
    </UserFormDialog>
  </main-layout>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import { getExecutives, createExecutive, updateExecutive, deleteExecutive } from '@/api/assetwise'
import DataTable from './DataTable.vue'
import UserFormDialog from './UserFormDialog.vue'
import UserForm from './UserForm.vue'

export default {
  name: 'AssetWiseExecutiveManagement',
  components: { DataTable, UserFormDialog, UserForm },
  data() {
    return {
      filter: {
        search: ''
      },
      userFormDialog: {
        isOpen: false,
        isLoading: false,
        key: 0,
        title: '',
        mode: 'create',
        data: null
      },
      result: {
        isLoading: false,
        items: [],
        pagination: {
          page: 1,
          rowsPerPage: 10,
          totalItems: 0
        }
      },
      debounceSearch: undefined,
      intervalId: undefined
    }
  },
  computed: {
    ...mapState(['selectedBot'])
  },
  created() {
    this.debounceSearch = debounce(() => {
      this.search()
      this.resetIntervalSearch()
    }, 2000)
    this.search()
    this.startIntervalSearch()
  },
  beforeDestroy() {
    this.stopIntervalSearch()
  },
  methods: {
    addUser() {
      Object.assign(this.userFormDialog, {
        isOpen: true,
        isLoading: false,
        key: Date.now(),
        title: '',
        mode: 'create',
        data: {
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
          tel: ''
        }
      })
      this.stopIntervalSearch()
    },
    changePassword(data) {
      Object.assign(this.userFormDialog, {
        isOpen: true,
        isLoading: false,
        key: Date.now(),
        title: data.username,
        mode: 'password',
        data: { ...data }
      })
      this.stopIntervalSearch()
    },
    async setConnection(data, value) {
      if (value) return

      try {
        this.stopIntervalSearch()

        const { value } = await this.$swal.fire({
          type: 'question',
          title: this.$t('asset_wise.disconnect_question', [data.username]),
          showCancelButton: true,
          confirmButtonText: this.$t('Confirm'),
          cancelButtonText: this.$t('cancel'),
          reverseButtons: true,
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            this.$swal.getCancelButton().style.display = 'none'
            await updateExecutive({
              page_id: this.selectedBot.pageid,
              username: data.username,
              is_deactive: true
            })
            return true
          },
          allowOutsideClick: () => !this.$swal.isLoading(),
          allowEscapeKey: () => !this.$swal.isLoading()
        })

        if (!value) return

        this.search()
        this.resetIntervalSearch()
      } catch (error) {
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      } finally {
        this.resetIntervalSearch()
      }
    },
    editUser(data) {
      Object.assign(this.userFormDialog, {
        isOpen: true,
        isLoading: false,
        key: Date.now(),
        title: data.username,
        mode: 'edit',
        data: { ...data }
      })
      this.stopIntervalSearch()
    },
    async removeUser(data) {
      try {
        this.stopIntervalSearch()

        const { value } = await this.$swal.fire({
          type: 'question',
          title: this.$t('Do you want to delete {name}?', { name: data.username }),
          showCancelButton: true,
          confirmButtonText: this.$t('Confirm'),
          cancelButtonText: this.$t('cancel'),
          reverseButtons: true,
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            this.$swal.getCancelButton().style.display = 'none'
            await deleteExecutive({
              page_id: this.selectedBot.pageid,
              username: data.username
            })
            return true
          },
          allowOutsideClick: () => !this.$swal.isLoading(),
          allowEscapeKey: () => !this.$swal.isLoading()
        })

        if (!value) return

        this.search()
      } catch (error) {
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      } finally {
        this.resetIntervalSearch()
      }
    },
    async search(page = 1) {
      try {
        this.result.isLoading = true

        const { data } = await getExecutives({
          page_id: this.selectedBot.pageid,
          limit: this.result.pagination.rowsPerPage,
          offset: (page - 1) * this.result.pagination.rowsPerPage,
          username: this.filter.search
        })

        const { data: items, total: totalItems } = data

        this.result.items = items.map((item, index) => ({
          id: (page - 1) * this.result.pagination.rowsPerPage + index,
          username: item.username || '',
          password: item.password || '',
          firstName: item.name || '',
          lastName: item.sur_name || '',
          email: item.email || '',
          tel: item.phone || '',
          isConnected: item.is_connect && item.user_id
        }))

        this.result.pagination = {
          ...this.result.pagination,
          page,
          totalItems
        }
      } catch (error) {
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      } finally {
        this.result.isLoading = false
      }
    },
    async submitUserForm(done) {
      const isValid = await this.$refs.userForm.$validator.validate()
      if (!isValid) return

      try {
        this.userFormDialog.isLoading = true

        const { data: formData, mode } = this.userFormDialog

        const payload = {
          page_id: this.selectedBot.pageid,
          name: formData.firstName,
          sur_name: formData.lastName,
          email: formData.email,
          phone: formData.tel
        }

        switch (mode) {
          case 'create':
            await createExecutive({
              ...payload,
              username: formData.username,
              password: formData.password
            })
            break
          case 'edit':
            await updateExecutive({ ...payload, username: formData.username })
            break
          case 'password':
            await updateExecutive({
              ...payload,
              username: formData.username,
              password: formData.password
            })
            await updateExecutive({
              page_id: this.selectedBot.pageid,
              username: formData.username,
              is_deactive: true
            })
            break
        }

        done()
        this.userFormDialog.isOpen = false
        this.search()
      } catch (error) {
        this.userFormDialog.isLoading = false

        const { status } = error.response
        if (status === 409) {
          const { data = {} } = error.response
          const { message: messageString } = data
          const messages = messageString.split(' | ')
          const fields = messages.map((message) =>
            message.replace(/^(.*) is.*$/, '$1').toLowerCase()
          )
          const transformedFields = fields.map(
            (field) =>
              ({
                username: this.$t('asset_wise.username'),
                email: this.$t('email'),
                phone: this.$t('Phone')
              }[field])
          )

          this.$swal(
            this.$t('asset_wise.executive_user_existence_error_title'),
            this.$t('asset_wise.executive_user_existence_error_text', [
              transformedFields.join(', ')
            ]),
            'error'
          )
        } else {
          this.$swal(this.$t('Something went wrong'), undefined, 'error')
        }

        console.error(error)
      }
    },
    handleUserFormDialogInput(value) {
      if (!value) {
        this.resetIntervalSearch()
      }
    },
    startIntervalSearch() {
      this.intervalId = setInterval(() => {
        this.search(this.pagination.page)
      }, 10000)
    },
    stopIntervalSearch() {
      clearInterval(this.intervalId)
    },
    resetIntervalSearch() {
      clearInterval(this.intervalId)
      this.startIntervalSearch()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/template/AssetWise/ExecutiveManagement/index.vue